import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, TrendingUp, Target, Clock, CheckCircle,
    AlertCircle, Lightbulb, Activity, ChevronDown, ChevronUp,
    Calendar, Filter, BarChart3, PieChart, Trophy, Zap
} from 'lucide-react';

const ParentReport = () => {
    const { studentId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [performance, setPerformance] = useState(null);
    const [studentName, setStudentName] = useState('');

    const [suggestions, setSuggestions] = useState([]);

    // New state for advanced features
    const [viewType, setViewType] = useState('weekly'); // daily, weekly, monthly, lastmonth
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [expandedGames, setExpandedGames] = useState(new Set());

    const API_URL = process.env.REACT_APP_API_BASE_URL;


    useEffect(() => {
        const verifyAccess = async () => {
            const token = localStorage.getItem('token');
            const deviceId = localStorage.getItem('deviceId');
            if (!token) {
                navigate('/signin');
                return;
            }

            try {
                const response = await axios.get(`${API_URL}/auth/verify`, {
                    headers: {
                        Authorization: token,
                        'X-Device-Id': deviceId
                    }
                });

                const { role, userId } = response.data;
                const isStudent = role === 'student';
                const isAuthorized = role === 'teacher' || role === 'admin' || (isStudent && String(userId) === String(studentId));

                if (!isAuthorized) {
                    alert('Access denied. You can only view your own report or require Teacher/Admin access.');
                    navigate('/');
                    return;
                }

                loadReportData();
            } catch (err) {
                console.error("Access verification failed:", err);
                navigate('/signin');
            }
        };

        verifyAccess();
    }, [studentId]);

    const loadReportData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const deviceId = localStorage.getItem('deviceId');
            const headers = { Authorization: token, 'X-Device-Id': deviceId };

            const perfRes = await axios.get(`${API_URL}/admin/performance/${studentId}`, { headers });
            const perfData = Array.isArray(perfRes.data) ? perfRes.data[0] : perfRes.data;

            // Try to fetch student name (non-critical — may fail for student role)
            try {
                const studentsRes = await axios.get(`${API_URL}/admin/students`, { headers });
                const students = studentsRes.data?.students || studentsRes.data || [];
                const found = students.find(s =>
                    String(s._id) === String(studentId) ||
                    String(s.userId) === String(studentId)
                );
                if (found) {
                    setStudentName(
                        found.name ||
                        `${found.firstName || ''} ${found.lastName || ''}`.trim() ||
                        found.email || studentId
                    );
                } else {
                    setStudentName(studentId);
                }
            } catch {
                setStudentName(studentId);
            }

            setPerformance(perfData || {});
            generateSuggestions(perfData || {});
        } catch (err) {
            console.error('Error loading report:', err);
            setError('Failed to load report data.');
        } finally {
            setLoading(false);
        }
    };

    // --- DATA PROCESSING LOGIC ---

    const filteredHistory = useMemo(() => {
        if (!performance?.history) return [];

        let start, end;
        const now = new Date();

        if (viewType === 'daily') {
            // Today or selected date — full day
            const base = selectedDate ? new Date(selectedDate) : new Date();
            start = new Date(base.getFullYear(), base.getMonth(), base.getDate(), 0, 0, 0, 0);
            end = new Date(base.getFullYear(), base.getMonth(), base.getDate(), 23, 59, 59, 999);

        } else if (viewType === 'weekly') {
            // Last 7 days rolling window
            end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
            start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6, 0, 0, 0, 0);

        } else if (viewType === 'monthly') {
            // Last 30 days rolling window
            end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
            start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29, 0, 0, 0, 0);

        } else if (viewType === 'lastmonth') {
            // Previous calendar month (e.g. if today is April, show March 1–31)
            start = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
            end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
        }

        return performance.history.filter(h => {
            // Support both timestamp (Date object / ISO string) and date string ("YYYY-MM-DD")
            let hDate;
            if (h.timestamp) {
                hDate = new Date(h.timestamp);
            } else if (h.date) {
                const parts = String(h.date).split('T')[0].split('-');
                hDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
            } else {
                return false;
            }
            return !isNaN(hDate.getTime()) && hDate >= start && hDate <= end;
        });
    }, [performance, viewType, selectedDate]);


    const aggregatedData = useMemo(() => {
        const games = {};
        let totalTime = 0;
        let totalPlays = 0;
        let totalCorrect = 0;
        let totalQuestions = 0;

        filteredHistory.forEach(session => {
            const name = session.gameType || 'unknown';
            const diff = (session.difficulty || 'medium').toLowerCase();
            // Calculate time: use timeSpentSeconds if available, otherwise derive from speed & count
            // speed = questions per second, count = number of questions → time = count / speed
            let time = 0;
            if (session.timeSpentSeconds && session.timeSpentSeconds > 0) {
                time = parseFloat(session.timeSpentSeconds);
            } else if (session.speed && session.speed > 0 && session.count) {
                time = Math.round(parseFloat(session.count) / parseFloat(session.speed));
            } else if (session.speed && session.speed > 0 && session.totalQuestions) {
                time = Math.round(parseFloat(session.totalQuestions) / parseFloat(session.speed));
            }

            // Normalize field names: check for both old (correct/total) and new (correctAnswers/totalQuestions)
            const correct = parseInt(session.correctAnswers || session.correct) || 0;
            const questions = parseInt(session.totalQuestions || session.total) || 1;

            if (!games[name]) {
                games[name] = {
                    name,
                    totalTime: 0,
                    totalPlays: 0,
                    totalCorrect: 0,
                    totalQuestions: 0,
                    difficulties: {},
                    sessions: []
                };
            }

            const g = games[name];
            g.totalTime += time;
            g.totalPlays += 1;
            g.totalCorrect += correct;
            g.totalQuestions += questions;

            if (!g.difficulties[diff]) {
                g.difficulties[diff] = { plays: 0, correct: 0, questions: 0, time: 0 };
            }
            g.difficulties[diff].plays += 1;
            g.difficulties[diff].correct += correct;
            g.difficulties[diff].questions += questions;
            g.difficulties[diff].time += time;

            g.sessions.push(session);

            totalTime += time;
            totalPlays += 1;
            totalCorrect += correct;
            totalQuestions += questions;
        });

        // Calculate Strengths and Challenges
        let strongest = null, weakest = null;
        Object.values(games).forEach(g => {
            const acc = g.totalQuestions > 0 ? (g.totalCorrect / g.totalQuestions) : 0;
            if (!strongest || acc > strongest.acc) strongest = { name: g.name, acc };
            if (!weakest || acc < weakest.acc) weakest = { name: g.name, acc };
        });

        return { games, totalTime, totalPlays, totalCorrect, totalQuestions, strongest, weakest };
    }, [filteredHistory]);

    // --- HELPER FUNCTIONS ---

    const formatTime = (seconds) => {
        if (!seconds) return "0s";
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = seconds % 60;
        if (h > 0) return `${h}h ${m}m`;
        if (m > 0) return `${m}m ${s}s`;
        return `${s}s`;
    };

    const toggleExpand = (gameName) => {
        const newExpanded = new Set(expandedGames);
        if (newExpanded.has(gameName)) newExpanded.delete(gameName);
        else newExpanded.add(gameName);
        setExpandedGames(newExpanded);
    };

    const generateSuggestions = (perf) => {
        // ... (Keep existing suggestion logic or enhance later)
        setSuggestions([{ type: 'info', text: 'Keep up the consistent daily practice!' }]);
    };

    // --- RENDER HELPERS ---

    const renderTabs = () => (
        <div style={{ display: 'flex', background: '#f1f5f9', padding: '4px', borderRadius: '12px', marginBottom: '20px' }}>
            {[
                { key: 'daily', label: 'Today' },
                { key: 'weekly', label: 'Last 7 Days' },
                { key: 'monthly', label: 'Last 30 Days' },
                { key: 'lastmonth', label: 'Last Month' },
            ].map(t => (
                <button
                    key={t.key}
                    onClick={() => setViewType(t.key)}
                    style={{
                        flex: 1,
                        padding: '10px',
                        border: 'none',
                        borderRadius: '8px',
                        background: viewType === t.key ? 'white' : 'transparent',
                        color: viewType === t.key ? '#4f46e5' : '#64748b',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        boxShadow: viewType === t.key ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
                        transition: '0.2s'
                    }}
                >
                    {t.label}
                </button>
            ))}
        </div>
    );


    const renderFilters = () => {
        if (viewType === 'daily') {
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Calendar size={18} color="#64748b" />
                    <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', color: '#1e293b' }}
                    />
                </div>
            );
        }
        return null;
    };

    if (loading) return <div style={{ padding: '50px', textAlign: 'center' }}>Loading Report...</div>;

    const { games, totalTime, totalPlays, totalCorrect, totalQuestions, strongest, weakest } = aggregatedData;
    const overallAccuracy = totalQuestions > 0 ? ((totalCorrect / totalQuestions) * 100).toFixed(1) : 0;

    return (
        <div style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto', fontFamily: 'system-ui, -apple-system, sans-serif', color: '#1e293b' }}>
            <button
                onClick={() => navigate(-1)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', color: '#64748b' }}
            >
                <ArrowLeft size={16} /> Dashboard
            </button>

            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                <h1 style={{ fontSize: '2.2rem', fontWeight: '800', marginBottom: '4px', color: '#0f172a' }}>Advanced Analytics</h1>
                {studentName && (
                    <div style={{
                        display: 'inline-flex', alignItems: 'center', gap: '8px',
                        background: '#eef2ff', padding: '6px 16px', borderRadius: '999px',
                        marginBottom: '8px'
                    }}>
                        <span style={{ fontSize: '1rem', fontWeight: '700', color: '#4f46e5' }}>👤 {studentName}</span>
                    </div>
                )}
                <p style={{ color: '#64748b' }}>Deep dive into student performance and habits</p>
            </div>

            {renderTabs()}

            {/* Show Current Range */}
            <div style={{
                marginBottom: '20px',
                padding: '12px 16px',
                background: 'white',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Calendar size={18} color="#6366f1" />
                    <span style={{ fontWeight: '600', color: '#475569' }}>
                        {(() => {
                            if (filteredHistory.length === 0) return 'No data in selected range';
                            const getDt = h => h.timestamp ? new Date(h.timestamp) : (() => { const p = String(h.date).split('T')[0].split('-'); return new Date(+p[0], +p[1] - 1, +p[2]); })();
                            const dates = filteredHistory.map(getDt).filter(d => !isNaN(d));
                            const min = new Date(Math.min(...dates));
                            const max = new Date(Math.max(...dates));
                            return `${min.toLocaleDateString('en-IN')} – ${max.toLocaleDateString('en-IN')} (${filteredHistory.length} sessions)`;
                        })()}
                    </span>
                </div>
                {renderFilters()}
            </div>

            {/* Performance Highlights */}
            {totalPlays > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
                    <div style={{ background: '#f0fdf4', padding: '16px', borderRadius: '12px', border: '1px solid #bcf0da' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#166534', marginBottom: '8px' }}>
                            <Trophy size={20} />
                            <span style={{ fontWeight: '700' }}>Biggest Strength</span>
                        </div>
                        <p style={{ fontSize: '1.1rem', color: '#064e3b', fontWeight: 'bold' }}>{strongest?.name || 'N/A'}</p>
                        <p style={{ fontSize: '0.85rem', color: '#166534' }}>Top accuracy among all practiced operations</p>
                    </div>
                    <div style={{ background: '#fff7ed', padding: '16px', borderRadius: '12px', border: '1px solid #fed7aa' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#9a3412', marginBottom: '8px' }}>
                            <Zap size={20} />
                            <span style={{ fontWeight: '700' }}>Area for Improvement</span>
                        </div>
                        <p style={{ fontSize: '1.1rem', color: '#7c2d12', fontWeight: 'bold' }}>{weakest?.name || 'N/A'}</p>
                        <p style={{ fontSize: '0.85rem', color: '#9a3412' }}>Needs more practice to improve consistency</p>
                    </div>
                </div>
            )}

            {/* 
            {performance?.totalGames > (performance?.history?.length || 0) && (
                <div style={{
                    marginBottom: '20px',
                    padding: '14px 18px',
                    background: '#fefce8',
                    borderRadius: '12px',
                    border: '1px solid #fde047',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px'
                }}>
                    <AlertCircle size={20} color="#ca8a04" style={{ flexShrink: 0, marginTop: '2px' }} />
                    <div>
                        <div style={{ fontWeight: '700', color: '#713f12', marginBottom: '4px' }}>
                            All-Time Total: {performance.totalGames.toLocaleString()} games played
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#92400e' }}>
                            Detailed session history shows the most recent <strong>{performance?.history?.length || 0} sessions</strong> only.
                            The system now stores up to 10,000 sessions — all future games will be fully tracked.
                        </div>
                    </div>
                </div>
            )}*/}

            {/* { Summary Row } */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
                <div style={{ background: 'white', padding: '20px', borderRadius: '16px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}><Clock color="#6366f1" /></div>
                    <div style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '4px' }}>Time (This Period)</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{formatTime(totalTime)}</div>
                </div>
                <div style={{ background: 'white', padding: '20px', borderRadius: '16px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}><Target color="#10b981" /></div>
                    <div style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '4px' }}>Avg. Accuracy</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{overallAccuracy}%</div>
                </div>
                <div style={{ background: 'white', padding: '20px', borderRadius: '16px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}><Activity color="#f59e0b" /></div>
                    <div style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '4px' }}>Sessions (This Period)</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{totalPlays}</div>
                </div>
                <div style={{ background: 'linear-gradient(135deg, #4f46e5, #7c3aed)', padding: '20px', borderRadius: '16px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}><Trophy color="white" /></div>
                    <div style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.8)', marginBottom: '4px' }}>All-Time Total</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'white' }}>{(performance?.totalGames || 0).toLocaleString()}</div>
                </div>
            </div>

            {/* Game List */}
            <h2 style={{ fontSize: '1.3rem', fontWeight: '700', marginBottom: '15px', color: '#0f172a' }}>Game Performance Breakdown</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {Object.values(games).length === 0 ? (
                    <div style={{ padding: '40px', textAlign: 'center', background: '#f8fafc', borderRadius: '16px', border: '1px dashed #cbd5e1' }}>
                        No sessions found for this period.
                    </div>
                ) : (
                    Object.values(games).map(game => {
                        const isExpanded = expandedGames.has(game.name);
                        const gameAccuracy = ((game.totalCorrect / game.totalQuestions) * 100).toFixed(1);

                        return (
                            <div key={game.name} style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                                {/* Summary Strip */}
                                <div
                                    onClick={() => toggleExpand(game.name)}
                                    style={{ padding: '15px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', transition: '0.2s' }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                        <div style={{ padding: '10px', background: '#eef2ff', borderRadius: '12px' }}>
                                            <PieChart size={20} color="#4338ca" />
                                        </div>
                                        <div>
                                            <div style={{ fontWeight: '700', textTransform: 'capitalize', fontSize: '1.1rem' }}>{game.name.replace(/([A-Z])/g, ' $1').trim()}</div>
                                            <div style={{ fontSize: '0.85rem', color: '#64748b' }}>{game.totalPlays} sessions</div>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '40px' }}>
                                        <div style={{ textAlign: 'right', display: 'none', md: 'block' }}>
                                            <div style={{ fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase' }}>Accuracy</div>
                                            <div style={{ fontWeight: '700', color: gameAccuracy > 80 ? '#10b981' : gameAccuracy > 50 ? '#f59e0b' : '#ef4444' }}>{gameAccuracy}%</div>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase' }}>Time</div>
                                            <div style={{ fontWeight: '700' }}>{formatTime(game.totalTime)}</div>
                                        </div>
                                        {isExpanded ? <ChevronUp size={20} color="#94a3b8" /> : <ChevronDown size={20} color="#94a3b8" />}
                                    </div>
                                </div>

                                {/* Expanded Content */}
                                {isExpanded && (
                                    <div style={{ padding: '20px', background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                                        {/* Difficulty Breakdown */}
                                        <div style={{ marginBottom: '25px' }}>
                                            <h4 style={{ fontSize: '0.9rem', textTransform: 'uppercase', color: '#64748b', letterSpacing: '0.05em', marginBottom: '12px' }}>Difficulty Breakdown</h4>
                                            <div style={{ overflowX: 'auto' }}>
                                                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                                                    <thead>
                                                        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                                                            <th style={{ padding: '10px', fontSize: '0.85rem', color: '#94a3b8' }}>Level</th>
                                                            <th style={{ padding: '10px', fontSize: '0.85rem', color: '#94a3b8' }}>Plays</th>
                                                            <th style={{ padding: '10px', fontSize: '0.85rem', color: '#94a3b8' }}>Accuracy</th>
                                                            <th style={{ padding: '10px', fontSize: '0.85rem', color: '#94a3b8' }}>Avg. Time</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {Object.entries(game.difficulties).map(([lvl, stats]) => (
                                                            <tr key={lvl} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                                <td style={{ padding: '10px', fontWeight: '600', textTransform: 'capitalize' }}>{lvl}</td>
                                                                <td style={{ padding: '10px' }}>{stats.plays}</td>
                                                                <td style={{ padding: '10px', fontWeight: '600' }}>{((stats.correct / stats.questions) * 100).toFixed(1)}%</td>
                                                                <td style={{ padding: '10px' }}>{formatTime(Math.round(stats.time / stats.plays))}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>

                                        {/* Session List */}
                                        <div>
                                            <h4 style={{ fontSize: '0.9rem', marginBottom: '10px', color: '#475569' }}>Recent Sessions (Last 20)</h4>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                {game.sessions.slice(-20).reverse().map((s, idx) => {
                                                    const sAcc = (parseInt(s.totalQuestions || s.total) || 0) > 0
                                                        ? (((parseInt(s.correctAnswers || s.correct) || 0) / (parseInt(s.totalQuestions || s.total) || 1)) * 100).toFixed(0)
                                                        : 0;
                                                    // Derive display date from timestamp or date string
                                                    const sDate = s.timestamp
                                                        ? new Date(s.timestamp).toLocaleDateString()
                                                        : (s.date ? String(s.date).split('T')[0] : 'N/A');
                                                    // Derive time from timeSpentSeconds or speed+count
                                                    let sTime = 0;
                                                    if (s.timeSpentSeconds > 0) sTime = s.timeSpentSeconds;
                                                    else if (s.speed > 0 && s.count) sTime = Math.round(s.count / s.speed);
                                                    else if (s.speed > 0 && s.totalQuestions) sTime = Math.round(s.totalQuestions / s.speed);
                                                    return (
                                                        <div key={idx} style={{ background: 'white', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                                                                <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{sDate}</div>
                                                                <div style={{ fontSize: '0.8rem', padding: '2px 8px', background: '#f1f5f9', borderRadius: '4px', textTransform: 'capitalize' }}>{s.difficulty}</div>
                                                            </div>
                                                            <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                                                                <div style={{ fontSize: '0.9rem', fontWeight: '700' }}>{sAcc}%</div>
                                                                <div style={{ fontSize: '0.9rem', color: '#64748b' }}>{formatTime(sTime)}</div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {/* Suggestions at bottom */}
            <div style={{ marginTop: '40px', padding: '25px', background: '#eff6ff', borderRadius: '20px', border: '1px solid #bfdbfe' }}>
                <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#1e3a8a', marginBottom: '10px' }}>
                    <Lightbulb size={20} /> Smart Insights
                </h3>
                {suggestions.map((s, i) => (
                    <p key={i} style={{ color: '#1e40af', margin: 0, fontSize: '0.95rem' }}>{s.text}</p>
                ))}
            </div>
        </div>
    );
};

export default ParentReport;
